import React, { useState } from "react";
import { Shield, Plus, Trash2, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import PlanRestrictionAlert from "./PlanRestrictionAlert";

const ROLE_OPTIONS = ["admin", "user"];

export default function SSOConfigSection({
    ssoEnabled,
    ssoRoleMappings,
    ssoClaimMappings,
    onSSOToggle,
    onRoleMappingsChange,
    onClaimMappingsChange,
    isUpdating,
    isSSOFeatureEnabled,
}) {
    const { t } = useTranslation();
    const mappings = ssoRoleMappings || {};
    const claims = ssoClaimMappings || {};
    const [newGroup, setNewGroup] = useState("");
    const [newRole, setNewRole] = useState("user");
    const [newClaimGroup, setNewClaimGroup] = useState("");
    const [newField, setNewField] = useState("");
    const [newValue, setNewValue] = useState("");

    const handleAddMapping = () => {
        if (!newGroup.trim()) return;
        onRoleMappingsChange({ ...mappings, [newGroup.trim()]: newRole });
        setNewGroup("");
        setNewRole("user");
    };

    const handleRemoveMapping = (group) => {
        const updated = { ...mappings };
        delete updated[group];
        onRoleMappingsChange(updated);
    };

    const handleAddClaim = () => {
        if (!newClaimGroup.trim() || !newField.trim() || !newValue.trim()) return;
        const updated = { ...claims, [newClaimGroup.trim()]: { field: newField.trim(), value: newValue.trim() } };
        onClaimMappingsChange(updated);
        setNewClaimGroup("");
        setNewField("");
        setNewValue("");
    };

    const handleRemoveClaim = (group) => {
        const updated = { ...claims };
        delete updated[group];
        onClaimMappingsChange(updated);
    };

    const disabled = !isSSOFeatureEnabled || isUpdating;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-gray-600" />
                {t("ssoConfig.title")}
            </h2>
            {!isSSOFeatureEnabled && (
                <PlanRestrictionAlert variant="inline" titleFallback={t("ssoConfig.upgradeRequired")}
                    descriptionFallback={t("ssoConfig.upgradeDescription")} />
            )}
            <ToggleRow label={t("ssoConfig.enableSSO")}
                enabled={ssoEnabled} onToggle={onSSOToggle} disabled={disabled}
                description={ssoEnabled ? t("ssoConfig.enabledDescription") : t("ssoConfig.disabledDescription")} />
            {ssoEnabled && isSSOFeatureEnabled && (
                <>
                    <p className="text-xs text-gray-500 mb-3">
                        {t("ssoConfig.configureProvider")}
                    </p>
                    <GroupsSetupHelp t={t} />
                    <RoleMappingSection t={t} mappings={mappings} newGroup={newGroup} newRole={newRole}
                        setNewGroup={setNewGroup} setNewRole={setNewRole}
                        onAdd={handleAddMapping} onRemove={handleRemoveMapping} />
                    <FieldMappingSection t={t} claims={claims} newGroup={newClaimGroup} newField={newField}
                        newValue={newValue} setNewGroup={setNewClaimGroup} setNewField={setNewField}
                        setNewValue={setNewValue} onAdd={handleAddClaim} onRemove={handleRemoveClaim} />
                </>
            )}
        </div>
    );
}

function ToggleRow({ label, description, enabled, onToggle, disabled }) {
    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
            <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">{label}</h3>
                <p className="text-sm text-gray-500 mt-1">{description}</p>
            </div>
            <button onClick={onToggle} disabled={disabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${enabled ? "bg-blue-600" : "bg-gray-300"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
        </div>
    );
}

function GroupsSetupHelp({ t }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-4">
            <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                <Info className="w-3.5 h-3.5" />
                {t("ssoConfig.groupsSetupTitle")}
            </button>
            {open && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-gray-700 space-y-2">
                    <p>{t("ssoConfig.groupsSetupDescription")}</p>
                    <ul className="list-disc pl-4 space-y-1">
                        <li><strong>Cognito:</strong> {t("ssoConfig.cognitoSetup")}</li>
                        <li><strong>Okta:</strong> {t("ssoConfig.oktaSetup")}</li>
                        <li><strong>Azure AD:</strong> {t("ssoConfig.azureSetup")}</li>
                        <li>{t("ssoConfig.genericSetup")}</li>
                    </ul>
                </div>
            )}
        </div>
    );
}

function RoleMappingSection({ t, mappings, newGroup, newRole, setNewGroup, setNewRole, onAdd, onRemove }) {
    return (
        <div className="mb-5">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t("ssoConfig.groupToRoleMapping")}</h3>
            {Object.entries(mappings).map(([group, role]) => (
                <div key={group} className="flex items-center gap-2 mb-2">
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded flex-1 truncate">{group}</span>
                    <span className="text-sm text-gray-500">&rarr;</span>
                    <span className="text-sm font-medium bg-blue-50 px-2 py-1 rounded">{role}</span>
                    <button onClick={() => onRemove(group)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
            ))}
            <div className="flex items-center gap-2 mt-2">
                <input type="text" value={newGroup} onChange={(e) => setNewGroup(e.target.value)}
                    placeholder={t("ssoConfig.ssoGroupName")} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
                <select value={newRole} onChange={(e) => setNewRole(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm">
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={onAdd} disabled={!newGroup.trim()} className="text-blue-600 hover:text-blue-800 disabled:opacity-50">
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function FieldMappingSection({ t, claims, newGroup, newField, newValue, setNewGroup, setNewField, setNewValue, onAdd, onRemove }) {
    return (
        <div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">{t("ssoConfig.groupToFieldMapping")}</h3>
            <p className="text-xs text-gray-500 mb-2">{t("ssoConfig.fieldMappingDescription")}</p>
            {Object.entries(claims).map(([group, mapping]) => (
                <div key={group} className="flex items-center gap-2 mb-2">
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded flex-1 truncate">{group}</span>
                    <span className="text-sm text-gray-500">&rarr;</span>
                    <span className="text-sm bg-green-50 px-2 py-1 rounded truncate">{mapping?.field}</span>
                    <span className="text-sm text-gray-500">=</span>
                    <span className="text-sm font-medium bg-green-50 px-2 py-1 rounded truncate">{mapping?.value}</span>
                    <button onClick={() => onRemove(group)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
            ))}
            <div className="flex items-center gap-2 mt-2">
                <input type="text" value={newGroup} onChange={(e) => setNewGroup(e.target.value)}
                    placeholder={t("ssoConfig.ssoGroup")} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
                <input type="text" value={newField} onChange={(e) => setNewField(e.target.value)}
                    placeholder={t("ssoConfig.fieldName")} className="w-28 border border-gray-300 rounded px-2 py-1 text-sm" />
                <input type="text" value={newValue} onChange={(e) => setNewValue(e.target.value)}
                    placeholder={t("ssoConfig.value")} className="w-28 border border-gray-300 rounded px-2 py-1 text-sm" />
                <button onClick={onAdd} disabled={!newGroup.trim() || !newField.trim() || !newValue.trim()}
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50">
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
